import React, { useState, useEffect } from 'react';
import { 
  Bot, 
  MessageSquare, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Search, 
  Filter,
  Globe,
  Clock,
  Settings,
  BarChart3,
  Languages,
  AlertCircle,
  CheckCircle,
  MousePointer,
  Download,
  Upload,
  CheckSquare,
  Square,
  MoreHorizontal,
  Copy,
  Archive,
  RefreshCw,
  File
} from 'lucide-react';
import { whatsappAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import FileUpload from '../Upload/FileUpload';

// Safe JSON parse helper
const safeParse = (value, fallback) => {
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const WhatsAppAutoReply = () => {
  const [activeTab, setActiveTab] = useState('faqs');
  const [faqs, setFaqs] = useState([]);
  const [autoReplies, setAutoReplies] = useState([]);
  const [groupedAutoReplies, setGroupedAutoReplies] = useState([]);
  const [aiAssistants, setAiAssistants] = useState([]);
  

  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    trigger: '',
    response: '',
    category: 'general',
    language: 'en',
    priority: 1,
    conditions: {},
    // Advanced auto-reply features
    replyType: 'text',
    header: '',
    footer: '',
    aiAssistant: false,
    aiModel: 'gpt-3.5-turbo',
    aiContext: '',
    buttons: [],
    buttonLinks: [],
    mediaUrl: '',
    mediaType: 'image',
    templateName: '',
    variables: [],
    conditions: {},
    scheduleEnabled: false,
    scheduleStart: '',
    scheduleEnd: '',
    timezone: 'UTC',
    responseDelay: 0,
    maxResponses: 1,
    responseCount: 0,
    // New trigger types and message options
    triggerType: 'exact_match',
    messageOption: 'text',
    aiAssistantId: null,
    replyButtons: [],
    buttonLinks: [],
    files: [],
    options: [],
    subtext: '',
    submitButtonText: 'Submit',
    caseSensitive: false,
    regexPattern: '',
    isActive: true,
    stopTrigger: '',
    aiInitialMessage: ''
  });
  const [settings, setSettings] = useState({
    autoReplyEnabled: false,
    greetingEnabled: false,
    afterHoursEnabled: false,
    defaultLanguage: 'en',
    businessHoursStart: '09:00',
    businessHoursEnd: '18:00'
  });

  // Bulk actions state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importType, setImportType] = useState('faqs');
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [showInactiveItems, setShowInactiveItems] = useState(true);

  useEffect(() => {
    loadData();
    loadSettings();
  }, []);

  // Force reload settings when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      loadSettings();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const loadData = async (forceRefresh = false) => {
    setLoading(true);
    try {
      // Add cache-busting parameter if force refresh is requested
      const cacheBuster = forceRefresh ? `?t=${Date.now()}` : '';
      
      const [faqsRes, autoRepliesRes, aiAssistantsRes] = await Promise.all([
        whatsappAPI.getFaqs(cacheBuster),
        whatsappAPI.getAutoReplies(cacheBuster),
        whatsappAPI.getAiAssistants(cacheBuster)
      ]);
      
      const faqsData = Array.isArray(faqsRes.data?.data) ? faqsRes.data.data : [];
      setFaqs(faqsData);
      
      const rawAutoReplies = Array.isArray(autoRepliesRes.data?.data) ? autoRepliesRes.data.data : [];
      
      // Handle grouped data if available
      if (autoRepliesRes.data?.grouped) {
        const groupedArray = Object.entries(autoRepliesRes.data.grouped).map(([trigger, replies]) => ({
          trigger,
          replies,
          count: replies.length,
          highestPriority: Math.max(...replies.map(r => r.priority || 1))
        }));
        setGroupedAutoReplies(groupedArray);
      }
      
      // Normalize auto-replies to expected frontend shape
      const autoRepliesData = rawAutoReplies.map((item) => ({
        ...item,
        // Ensure response is present for edit/view
        response: item.response || item.response_message || '',
        // Map AI assistant id
        aiAssistantId: item.ai_assistant_id ?? item.aiAssistantId ?? null,
        // Normalize booleans
        is_active: item.is_active === 1 || item.is_active === true,
        schedule_enabled: item.schedule_enabled === 1 || item.schedule_enabled === true,
        case_sensitive: item.case_sensitive === 1 || item.case_sensitive === true,
        // Parse JSON-encoded fields if needed
        reply_buttons: typeof item.reply_buttons === 'string' ? safeParse(item.reply_buttons, []) : (item.reply_buttons || []),
        // Also expose camelCase for UI consumption
        replyButtons: typeof item.reply_buttons === 'string' ? safeParse(item.reply_buttons, []) : (item.reply_buttons || []),
        button_links: typeof item.button_links === 'string' ? safeParse(item.button_links, []) : (item.button_links || []),
        files: typeof item.files === 'string' ? safeParse(item.files, []) : (item.files || []),
        options: typeof item.options === 'string' ? safeParse(item.options, []) : (item.options || []),
        variables: typeof item.variables === 'string' ? safeParse(item.variables, []) : (item.variables || []),
        conditions: typeof item.conditions === 'string' ? safeParse(item.conditions, {}) : (item.conditions || {}),
        stopTrigger: item.stop_trigger || '',
        aiInitialMessage: item.ai_initial_message || '',
        messageOption: item.message_option || item.messageOption || 'text'
      }));
      setAutoReplies(autoRepliesData);
      const aiAssistantsData = Array.isArray(aiAssistantsRes.data?.data) ? aiAssistantsRes.data.data : [];
      setAiAssistants(aiAssistantsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setFaqs([]);
      setAutoReplies([]);
      setAiAssistants([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async (forceRefresh = false) => {
    try {
      const response = await whatsappAPI.getAutoReplySettings();
      
      // Extract the actual settings data from the response
      let settingsData = {};
      
      if (response.data?.data) {
        // Response format: { success: true, data: { ... } }
        settingsData = response.data.data;
      } else if (response.data) {
        // Response format: { ... } (direct data)
        settingsData = response.data;
      }
      
      // Merge with existing settings to preserve any missing values
      setSettings(prevSettings => ({
        ...prevSettings,
        ...settingsData
      }));
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {

      
      // Ensure isActive is properly converted to 1/0 for database
      const submitData = {
        ...formData,
        is_active: formData.isActive ? 1 : 0,
        // Map camelCase to snake_case for backend
        ai_assistant_id: formData.aiAssistantId ?? null,
        response_message: formData.response,
        stop_trigger: formData.stopTrigger || null,
        ai_initial_message: formData.aiInitialMessage || null,
        // Persist selected option and reply buttons in expected backend fields
        messageOption: formData.messageOption,
        reply_buttons: Array.isArray(formData.replyButtons) ? formData.replyButtons : []
      };
      
      if (activeTab === 'faqs') {
        if (editingItem) {
          await whatsappAPI.updateFaq(editingItem.id, submitData);
        } else {
          await whatsappAPI.createFaq(submitData);
        }
      } else {
        if (editingItem) {
          await whatsappAPI.updateAutoReply(editingItem.id, submitData);
        } else {
          await whatsappAPI.createAutoReply(submitData);
        }
      }
      
      setShowAddForm(false);
      setEditingItem(null);
      resetForm();
      await loadData();
      
      // Show success message
      if (activeTab === 'faqs') {
        toast.success(editingItem ? 'FAQ updated successfully!' : 'FAQ created successfully!');
      } else {
        toast.success(editingItem ? 'Auto-reply updated successfully!' : 'Auto-reply created successfully!');
      }
    } catch (error) {
      console.error('Error saving:', error);
      // Show user-friendly error message
      if (error.response?.data?.message) {
        toast.error(`Failed to save: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Failed to save: ${error.message}`);
      } else {
        toast.error('Failed to save. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    console.log('🔍 Editing auto-reply item:', item);
    setEditingItem(item);
    setShowAddForm(true); // Show the form when editing
    setFormData({
      question: item.question || '',
      answer: item.answer || '',
      trigger: item.trigger_keyword || item.trigger || '',
      response: item.response || '',
      category: item.category || 'general',
      language: item.language || 'en',
      priority: item.priority || 1,
      conditions: item.conditions || {},
      // Advanced auto-reply features
      replyType: item.reply_type || 'text',
      header: item.header || '',
      footer: item.footer || '',
      aiAssistant: item.ai_assistant || false,
      aiModel: item.ai_model || 'gpt-3.5-turbo',
      aiContext: item.ai_context || '',
      // Ensure both columns map correctly for reply buttons UI
      replyButtons: Array.isArray(item.replyButtons)
        ? item.replyButtons
        : (typeof item.reply_buttons === 'string' ? safeParse(item.reply_buttons, []) : (item.reply_buttons || [])),
      // Persist selected message option
      messageOption: item.message_option || item.messageOption || 'text',
      // Other advanced fields
      buttonLinks: Array.isArray(item.button_links) ? item.button_links : (typeof item.button_links === 'string' ? safeParse(item.button_links, []) : []),
      files: Array.isArray(item.files) ? item.files : (typeof item.files === 'string' ? safeParse(item.files, []) : []),
      options: Array.isArray(item.options) ? item.options : (typeof item.options === 'string' ? safeParse(item.options, []) : []),
      variables: Array.isArray(item.variables) ? item.variables : (typeof item.variables === 'string' ? safeParse(item.variables, []) : []),
      stopTrigger: item.stop_trigger || '',
      aiInitialMessage: item.ai_initial_message || '',
      // Media fields - map from database field names
      mediaUrl: item.media_url || item.mediaUrl || '',
      mediaType: item.media_type || item.mediaType || 'image',
      templateName: item.template_name || item.templateName || '',
      // Additional fields that might be missing
      subtext: item.subtext || '',
      submitButtonText: item.submit_button_text || item.submitButtonText || 'Submit',
      caseSensitive: item.case_sensitive || false,
      triggerType: item.trigger_type || item.triggerType || 'exact_match',
      aiAssistantId: item.ai_assistant_id || item.aiAssistantId || null
    });
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      setLoading(true);
      if (type === 'faq') {
        const response = await whatsappAPI.deleteFaq(id);
      } else {
        const response = await whatsappAPI.deleteAutoReply(id);
      }
      
      // Force refresh the data immediately after successful delete
      await loadData(true); // Force refresh to bypass any caching
      
      // Show success message
      toast.success(`${type === 'faq' ? 'FAQ' : 'Auto-reply'} deleted successfully`);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(`Failed to delete ${type === 'faq' ? 'FAQ' : 'Auto-reply'}: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      trigger: '',
      response: '',
      category: 'general',
      language: 'en',
      priority: 1,
      conditions: {},
      // Advanced auto-reply features
      replyType: 'text',
      header: '',
      footer: '',
      aiAssistant: false,
      aiModel: 'gpt-3.5-turbo',
      aiContext: '',
      buttons: [],
      buttonLinks: [],
      mediaUrl: '',
      mediaType: 'image',
      templateName: '',
      variables: [],
      scheduleEnabled: false,
      scheduleStart: '',
      scheduleEnd: '',
      timezone: 'UTC',
      responseDelay: 0,
      maxResponses: 1,
      responseCount: 0,
      // New trigger types and message options
      triggerType: 'exact_match',
      messageOption: 'text',
      aiAssistantId: null,
      replyButtons: [],
      buttonLinks: [],
      files: [],
      options: [],
      subtext: '',
      submitButtonText: 'Submit',
      caseSensitive: false,
      regexPattern: '',
      isActive: true,
      stopTrigger: '',
      aiInitialMessage: ''
    });
  };

  const updateSetting = async (key, value) => {
    try {
      await whatsappAPI.updateAutoReplySettings({ [key]: value });
      
      // Update local state immediately
      setSettings(prev => ({ ...prev, [key]: value }));
      
      // Reload settings after a short delay to ensure database is updated
      setTimeout(() => {
        loadSettings();
      }, 500);
    } catch (error) {
      console.error('Error updating setting:', error);
    }
  };

  // Bulk Actions Functions
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      const currentItems = activeTab === 'faqs' ? faqs : autoReplies;
      setSelectedItems(currentItems.map(item => item.id));
      setSelectAll(true);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (!selectedItems.length) {
      toast.error('Please select items to delete');
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to delete ${selectedItems.length} ${activeTab === 'faqs' ? 'FAQs' : 'auto-replies'}?`);
    if (!confirmed) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const endpoint = activeTab === 'faqs' ? 'faqs' : 'auto-replies';
      await whatsappAPI.bulkDelete(endpoint, selectedItems);
      
      setSelectedItems([]);
      setSelectAll(false);
      await loadData(true);
      toast.success(`Successfully deleted ${selectedItems.length} ${activeTab === 'faqs' ? 'FAQs' : 'auto-replies'}`);
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete items. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkActivate = async () => {
    if (!selectedItems.length) {
      toast.error('Please select items to activate');
      return;
    }

    setBulkActionLoading(true);
    try {
      const endpoint = activeTab === 'faqs' ? 'faqs' : 'auto-replies';
      await whatsappAPI.bulkUpdate(endpoint, selectedItems, { is_active: true });
      
      setSelectedItems([]);
      setSelectAll(false);
      await loadData(true);
      toast.success(`Successfully activated ${selectedItems.length} ${activeTab === 'faqs' ? 'FAQs' : 'auto-replies'}`);
    } catch (error) {
      console.error('Bulk activate error:', error);
      toast.error('Failed to activate items. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (!selectedItems.length) {
      toast.error('Please select items to deactivate');
      return;
    }

    setBulkActionLoading(true);
    try {
      const endpoint = activeTab === 'faqs' ? 'faqs' : 'auto-replies';
      await whatsappAPI.bulkUpdate(endpoint, selectedItems, { is_active: false });
      
      setSelectedItems([]);
      setSelectAll(false);
      await loadData(true);
      toast.success(`Successfully deactivated ${selectedItems.length} ${activeTab === 'faqs' ? 'FAQs' : 'auto-replies'}`);
    } catch (error) {
      console.error('Bulk deactivate error:', error);
      toast.error('Failed to deactivate items. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkExport = async () => {
    if (!selectedItems.length) {
      toast.error('Please select items to export');
      return;
    }

    setBulkActionLoading(true);
    try {
      const endpoint = activeTab === 'faqs' ? 'faqs' : 'auto-replies';
      const response = await whatsappAPI.bulkExport(endpoint, selectedItems);
      
      // Create and download file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeTab}-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Successfully exported ${selectedItems.length} ${activeTab === 'faqs' ? 'FAQs' : 'auto-replies'}`);
    } catch (error) {
      console.error('Bulk export error:', error);
      toast.error('Failed to export items. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    setBulkActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('type', importType);
      
      const endpoint = activeTab === 'faqs' ? 'faqs' : 'auto-replies';
      await whatsappAPI.bulkImport(endpoint, formData);
      
      setShowImportModal(false);
      setImportFile(null);
      await loadData(true);
      toast.success('Successfully imported items');
    } catch (error) {
      console.error('Bulk import error:', error);
      toast.error('Failed to import items. Please check your file format.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/json') {
      setImportFile(file);
    } else {
      toast.error('Please select a valid JSON file');
    }
  };

  const handleFileUpload = (fileIndex, uploadedFiles) => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      const uploadedFile = uploadedFiles[0];
      const newFiles = [...formData.files];
      
      // Update the file at the specified index
      newFiles[fileIndex] = {
        name: uploadedFile.originalName || uploadedFile.name || 'Uploaded File',
        url: uploadedFile.url,
        type: getFileTypeFromMime(uploadedFile.mimeType || uploadedFile.type || uploadedFile.fileType)
      };
      
      setFormData({ ...formData, files: newFiles });
      setUploadingFiles(prev => ({ ...prev, [fileIndex]: false }));
      
      toast.success('File uploaded successfully!');
    }
  };

  const getFileTypeFromMime = (mimeType) => {
    if (!mimeType) return 'document';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const getAcceptTypes = (fileType) => {
    switch (fileType) {
      case 'image':
        return 'image/*';
      case 'video':
        return 'video/*';
      case 'audio':
        return 'audio/*';
      case 'document':
      default:
        return '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx';
    }
  };

  const renderFAQForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Question *
        </label>
        <input
          type="text"
          value={formData.question}
          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Answer *
        </label>
        <textarea
          value={formData.answer}
          onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          required
        />
      </div>

      {/* Advanced FAQ Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Trigger Type *
          </label>
          <select
            value={formData.triggerType}
            onChange={(e) => setFormData({ ...formData, triggerType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            required
          >
            <option value="exact_match">On Exact Match</option>
            <option value="contains">When Message Contains</option>
            <option value="starts_with">Starts With</option>
            <option value="ends_with">Ends With</option>
            <option value="regex_match">Regex Match</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Choose Option *
          </label>
          <select
            value={formData.messageOption}
            onChange={(e) => setFormData({ ...formData, messageOption: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            required
          >
            <option value="text">Text Message</option>
            <option value="ai_assistant">Personal AI Assistant</option>
            <option value="reply_buttons">Bot with Reply Buttons</option>
            <option value="button_links">Bot with Button Links (CTA)</option>
            <option value="file">Bot with File</option>
            <option value="options">Bot with Options</option>
            <option value="template">Template Message</option>
            <option value="media">Media Message</option>
          </select>
        </div>
      </div>

      {/* Case sensitivity and regex */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="faqCaseSensitive"
            checked={formData.caseSensitive}
            onChange={(e) => setFormData({ ...formData, caseSensitive: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="faqCaseSensitive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
            Case Sensitive
          </label>
        </div>
        {formData.triggerType === 'regex_match' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Regex Pattern
            </label>
            <input
              type="text"
              value={formData.regexPattern}
              onChange={(e) => setFormData({ ...formData, regexPattern: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="e.g., ^hello.*world$"
            />
          </div>
        )}
      </div>

      {/* Header/Footer for text */}
      {formData.messageOption === 'text' && (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Header (Optional)
            </label>
            <input
              type="text"
              value={formData.header}
              onChange={(e) => setFormData({ ...formData, header: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Message header..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Footer (Optional)
            </label>
            <input
              type="text"
              value={formData.footer}
              onChange={(e) => setFormData({ ...formData, footer: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Message footer..."
            />
          </div>
        </>
      )}

      {/* Template Message (FAQ) */}
      {formData.messageOption === 'template' && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-yellow-900 dark:text-yellow-100 mb-4">Template Message</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Template Name</label>
              <input
                type="text"
                value={formData.templateName}
                onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., order_update"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Language</label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="te">Telugu</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Variables (JSON array)</label>
            <textarea
              rows={2}
              value={Array.isArray(formData.variables) ? JSON.stringify(formData.variables) : (formData.variables || '')}
              onChange={(e) => {
                try {
                  const v = JSON.parse(e.target.value || '[]');
                  if (Array.isArray(v)) setFormData({ ...formData, variables: v });
                } catch {}
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder='["var1","var2"]'
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Enter template variables in order.</p>
          </div>
        </div>
      )}


      {/* Reply Buttons */}
      {formData.messageOption === 'reply_buttons' && (
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-green-900 dark:text-green-100 mb-4 flex items-center">
            <MousePointer className="h-5 w-5 mr-2" />
            Reply Buttons Configuration
          </h3>
          <div className="space-y-4">
            {formData.replyButtons.map((button, index) => (
              <div key={index} className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-700 rounded-lg">
                <input
                  type="text"
                  value={button.text}
                  onChange={(e) => {
                    const newButtons = [...formData.replyButtons];
                    newButtons[index].text = e.target.value;
                    setFormData({ ...formData, replyButtons: newButtons });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Button text"
                />
                <input
                  type="text"
                  value={button.response}
                  onChange={(e) => {
                    const newButtons = [...formData.replyButtons];
                    newButtons[index].response = e.target.value;
                    setFormData({ ...formData, replyButtons: newButtons });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Response message"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newButtons = formData.replyButtons.filter((_, i) => i !== index);
                    setFormData({ ...formData, replyButtons: newButtons });
                  }}
                  className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newButtons = [...formData.replyButtons, { text: '', response: '' }];
                setFormData({ ...formData, replyButtons: newButtons });
              }}
              className="w-full px-4 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-700 dark:hover:bg-green-900/40"
            >
              + Add Reply Button
            </button>
          </div>
        </div>
      )}

      {/* Button Links */}
      {formData.messageOption === 'button_links' && (
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-purple-900 dark:text-purple-100 mb-4 flex items-center">
            <MousePointer className="h-5 w-5 mr-2" />
            Button Links (CTA) Configuration
          </h3>
          <div className="space-y-4">
            {formData.buttonLinks.map((button, index) => (
              <div key={index} className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-700 rounded-lg">
                <input
                  type="text"
                  value={button.text}
                  onChange={(e) => {
                    const newButtons = [...formData.buttonLinks];
                    newButtons[index].text = e.target.value;
                    setFormData({ ...formData, buttonLinks: newButtons });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Button text"
                />
                <input
                  type="url"
                  value={button.url}
                  onChange={(e) => {
                    const newButtons = [...formData.buttonLinks];
                    newButtons[index].url = e.target.value;
                    setFormData({ ...formData, buttonLinks: newButtons });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="URL to redirect to"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newButtons = formData.buttonLinks.filter((_, i) => i !== index);
                    setFormData({ ...formData, buttonLinks: newButtons });
                  }}
                  className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newButtons = [...formData.buttonLinks, { text: '', url: '' }];
                setFormData({ ...formData, buttonLinks: newButtons });
              }}
              className="w-full px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 border border-purple-300 rounded-md hover:bg-purple-200 dark:text-purple-400 dark:bg-purple-900/20 dark:border-purple-700 dark:hover:bg-purple-900/40"
            >
              + Add Button Link
            </button>
          </div>
        </div>
      )}

      {/* Files */}
      {formData.messageOption === 'file' && (
        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-orange-900 dark:text-orange-100 mb-4 flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            File Configuration
          </h3>
          <div className="space-y-4">
            {formData.files.map((file, index) => (
              <div key={index} className="p-3 bg-white dark:bg-gray-700 rounded-lg space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={file.name}
                    onChange={(e) => {
                      const newFiles = [...formData.files];
                      newFiles[index].name = e.target.value;
                      setFormData({ ...formData, files: newFiles });
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="File name"
                  />
                  <select
                    value={file.type}
                    onChange={(e) => {
                      const newFiles = [...formData.files];
                      newFiles[index].type = e.target.value;
                      setFormData({ ...formData, files: newFiles });
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="document">Document</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const newFiles = formData.files.filter((_, i) => i !== index);
                      setFormData({ ...formData, files: newFiles });
                    }}
                    className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {/* Upload or URL */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Upload File or Enter URL
                  </label>
                  <div className="mb-2">
                    <FileUpload
                      multiple={false}
                      accept={getAcceptTypes(file.type)}
                      maxSize={50 * 1024 * 1024}
                      onUploadComplete={(uploadedFiles) => handleFileUpload(index, uploadedFiles)}
                      onUploadError={(error) => {
                        toast.error(`Upload failed: ${error}`);
                        setUploadingFiles(prev => ({ ...prev, [index]: false }));
                      }}
                      uploadType="general"
                      compact={true}
                      autoUpload={true}
                    />
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Or enter file URL directly:</div>
                  <input
                    type="url"
                    value={file.url}
                    onChange={(e) => {
                      const newFiles = [...formData.files];
                      newFiles[index].url = e.target.value;
                      setFormData({ ...formData, files: newFiles });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="https://example.com/file.pdf"
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newFiles = [...formData.files, { name: '', url: '', type: 'document' }];
                setFormData({ ...formData, files: newFiles });
              }}
              className="w-full px-4 py-2 text-sm font-medium text-orange-700 bg-orange-100 border border-orange-300 rounded-md hover:bg-orange-200 dark:text-orange-400 dark:bg-orange-900/20 dark:border-orange-700 dark:hover:bg-orange-900/40"
            >
              + Add File
            </button>
          </div>
        </div>
      )}

      {/* Options */}
      {formData.messageOption === 'options' && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-indigo-900 dark:text-indigo-100 mb-4 flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Options Configuration
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subtext (Optional)
              </label>
              <textarea
                value={formData.subtext}
                onChange={(e) => setFormData({ ...formData, subtext: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Additional text to display with options..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Submit Button Text
              </label>
              <input
                type="text"
                value={formData.submitButtonText}
                onChange={(e) => setFormData({ ...formData, submitButtonText: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Submit"
              />
            </div>
            {formData.options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-700 rounded-lg">
                <input
                  type="text"
                  value={option.text}
                  onChange={(e) => {
                    const newOptions = [...formData.options];
                    newOptions[index].text = e.target.value;
                    setFormData({ ...formData, options: newOptions });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Option text"
                />
                <input
                  type="text"
                  value={option.value}
                  onChange={(e) => {
                    const newOptions = [...formData.options];
                    newOptions[index].value = e.target.value;
                    setFormData({ ...formData, options: newOptions });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Option value"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newOptions = formData.options.filter((_, i) => i !== index);
                    setFormData({ ...formData, options: newOptions });
                  }}
                  className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newOptions = [...formData.options, { text: '', value: '' }];
                setFormData({ ...formData, options: newOptions });
              }}
              className="w-full px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-100 border border-indigo-300 rounded-md hover:bg-indigo-200 dark:text-indigo-400 dark:bg-indigo-900/20 dark:border-indigo-700 dark:hover:bg-indigo-900/40"
            >
              + Add Option
            </button>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="general">General</option>
            <option value="services">Services</option>
            <option value="pricing">Pricing</option>
            <option value="support">Support</option>
            <option value="delivery">Delivery</option>
            <option value="payment">Payment</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Language
          </label>
          <select
            value={formData.language}
            onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="te">Telugu</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Priority
          </label>
          <input
            type="number"
            min="1"
            max="10"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
        
        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Active (Enable this FAQ)
            </span>
          </label>
        </div>
      </div>

      {/* Template Preview */}
      {(formData.header || formData.answer || formData.footer || formData.mediaUrl || formData.replyButtons.length > 0 || formData.buttonLinks.length > 0) && (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border-2 border-dashed border-gray-300 dark:border-gray-600">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            📱 FAQ Preview
          </h4>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm max-w-sm mx-auto">
            {/* Header */}
            {formData.header && (
              <div className="text-center mb-3">
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded">
                  {formData.header}
                </div>
              </div>
            )}

            {/* Media */}
            {formData.mediaUrl && (
              <div className="mb-3">
                {formData.mediaType === 'image' ? (
                  <div className="text-center">
                    <img 
                      src={formData.mediaUrl} 
                      alt="Media" 
                      className="max-w-full h-32 object-cover rounded-lg mx-auto"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                      📎 Image (Failed to load)
                    </div>
                  </div>
                ) : formData.mediaType === 'video' ? (
                  <div className="text-center">
                    <video 
                      src={formData.mediaUrl} 
                      className="max-w-full h-32 object-cover rounded-lg mx-auto"
                      controls
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                      📎 Video (Failed to load)
                    </div>
                  </div>
                ) : formData.mediaType === 'audio' ? (
                  <div className="text-center">
                    <audio 
                      src={formData.mediaUrl} 
                      controls
                      className="w-full"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                      📎 Audio (Failed to load)
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                      📎 {formData.mediaType} Attachment
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Main Answer */}
            {formData.answer && (
              <div className="mb-3">
                <div className="text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                  {formData.answer}
                </div>
              </div>
            )}

            {/* Reply Buttons */}
            {formData.replyButtons.length > 0 && (
              <div className="mb-3">
                <div className="space-y-1">
                  {formData.replyButtons.map((button, index) => (
                    <div key={index} className="text-center">
                      <button className="w-full px-3 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
                        {button.text || `Button ${index + 1}`}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Button Links */}
            {formData.buttonLinks.length > 0 && (
              <div className="mb-3">
                <div className="space-y-1">
                  {formData.buttonLinks.map((button, index) => (
                    <div key={index} className="text-center">
                      <button className="w-full px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                        {button.text || `Link ${index + 1}`}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            {formData.footer && (
              <div className="text-center mt-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                  {formData.footer}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </form>
  );

  const renderAutoReplyForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Settings */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Trigger Keywords *
            </label>
            <input
              type="text"
              value={formData.trigger}
              onChange={(e) => setFormData({ ...formData, trigger: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="e.g., urgent, quote, meeting, help"
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Separate multiple keywords with commas
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Reply Type
            </label>
            <select
              value={formData.replyType}
              onChange={(e) => setFormData({ ...formData, replyType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="text">Text Message</option>
              <option value="template">Template Message</option>
              <option value="media">Media Message</option>
              <option value="interactive">Interactive Message</option>
              <option value="ai">AI Assistant</option>
            </select>
          </div>
        </div>

        {/* New Trigger Types and Message Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Trigger Type *
            </label>
            <select
              value={formData.triggerType}
              onChange={(e) => setFormData({ ...formData, triggerType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="exact_match">On Exact Match</option>
              <option value="contains">When Message Contains</option>
              <option value="welcome_reply">Welcome Reply</option>
              <option value="default_reply">Default Reply</option>
              <option value="regex_match">Regex Match</option>
              <option value="starts_with">Starts With</option>
              <option value="ends_with">Ends With</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              How the trigger keyword should be matched
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Choose Option *
            </label>
            <select
              value={formData.messageOption}
              onChange={(e) => setFormData({ ...formData, messageOption: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="text">Text Message</option>
              <option value="ai_assistant">Personal AI Assistant</option>
              <option value="reply_buttons">Bot with Reply Buttons</option>
              <option value="button_links">Bot with Button Links (CTA)</option>
              <option value="file">Bot with File</option>
              <option value="options">Bot with Options</option>
              <option value="template">Template Message</option>
              <option value="media">Media Message</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Type of message to send to the user
            </p>
          </div>
        </div>

        {/* Additional Trigger Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="caseSensitive"
              checked={formData.caseSensitive}
              onChange={(e) => setFormData({ ...formData, caseSensitive: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="caseSensitive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Case Sensitive
            </label>
          </div>
          
          {formData.triggerType === 'regex_match' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Regex Pattern
              </label>
              <input
                type="text"
                value={formData.regexPattern}
                onChange={(e) => setFormData({ ...formData, regexPattern: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., ^hello.*world$"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Language
            </label>
            <select
              value={formData.language}
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="en">English</option>
              <option value="hi">Hindi</option>
              <option value="te">Telugu</option>
              <option value="ta">Tamil</option>
              <option value="bn">Bengali</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Priority
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Responses
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.maxResponses}
              onChange={(e) => setFormData({ ...formData, maxResponses: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              How many times this auto-reply can be triggered
            </p>
          </div>
        </div>
        
        {/* Active/Inactive Toggle */}
        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Active (Enable this auto-reply)
            </span>
          </label>
        </div>
      </div>

      {/* Message Content */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Message Content</h3>
        
        {formData.replyType === 'text' && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Header (Optional)
              </label>
              <input
                type="text"
                value={formData.header}
                onChange={(e) => setFormData({ ...formData, header: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Message header..."
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Main Response *
              </label>
              <textarea
                value={formData.response}
                onChange={(e) => setFormData({ ...formData, response: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter your response message..."
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Footer (Optional)
              </label>
              <input
                type="text"
                value={formData.footer}
                onChange={(e) => setFormData({ ...formData, footer: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Message footer..."
              />
            </div>
          </>
        )}

        {formData.replyType === 'template' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Template Name
            </label>
            <input
              type="text"
              value={formData.templateName}
              onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter template name..."
            />
          </div>
        )}

        {formData.replyType === 'media' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Media Type
              </label>
              <select
                value={formData.mediaType}
                onChange={(e) => setFormData({ ...formData, mediaType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
                <option value="document">Document</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Media URL
              </label>
              <input
                type="url"
                value={formData.mediaUrl}
                onChange={(e) => setFormData({ ...formData, mediaUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="https://example.com/media.jpg"
              />
            </div>
          </div>
        )}
      </div>

      {/* AI Assistant Settings */}
      {(formData.replyType === 'ai' || formData.messageOption === 'ai_assistant') && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-4 flex items-center">
            <Bot className="h-5 w-5 mr-2" />
            AI Assistant Configuration
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.messageOption === 'ai_assistant' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select AI Assistant *
                </label>
                <select
                  value={formData.aiAssistantId || ''}
                  onChange={(e) => setFormData({ ...formData, aiAssistantId: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Select an AI Assistant</option>

                  {aiAssistants.map((assistant) => (
                    <option key={assistant.id} value={assistant.id}>
                      {assistant.name} ({assistant.model})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Choose from your configured AI assistants
                </p>
              </div>
            )}

            {formData.messageOption === 'ai_assistant' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    AI Initial Message *
                  </label>
                  <textarea
                    value={formData.aiInitialMessage}
                    onChange={(e) => setFormData({ ...formData, aiInitialMessage: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter the initial message when AI assistant is activated..."
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This message will be sent when the AI assistant session starts
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Stop Trigger Keyword *
                  </label>
                  <input
                    type="text"
                    value={formData.stopTrigger || ''}
                    onChange={(e) => setFormData({ ...formData, stopTrigger: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Stop AI, End Chat, Exit"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Keyword to stop the AI assistant session. This will be shown as a button in AI responses.
                  </p>
                </div>
              </>
            )}
            
            {formData.messageOption !== 'ai_assistant' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    AI Model
                  </label>
                  <select
                    value={formData.aiModel}
                    onChange={(e) => setFormData({ ...formData, aiModel: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="auto">🔄 Auto (Smart Model Selection)</option>
                    
                    <optgroup label="Latest GPT-5 Models (2024-2025)">
                      <option value="gpt-5">GPT-5 (Latest & Most Advanced - 2024)</option>
                      <option value="gpt-5-turbo">GPT-5 Turbo (High Performance - 2024)</option>
                      <option value="gpt-5o">GPT-5o (Latest Omni Model - 2024)</option>
                      <option value="gpt-5o-mini">GPT-5o Mini (Fast & Cost-effective - 2024)</option>
                    </optgroup>
                    
                    <optgroup label="Latest GPT-4 Models (2024-2025)">
                      <option value="gpt-4o">GPT-4o (Latest & Most Capable - 2024)</option>
                      <option value="gpt-4o-mini">GPT-4o Mini (Fast & Cost-effective - 2024)</option>
                      <option value="gpt-4-turbo-preview">GPT-4 Turbo Preview (Latest Features - 2024)</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo (High Performance)</option>
                      <option value="gpt-4">GPT-4 (Standard)</option>
                      <option value="gpt-4-32k">GPT-4 32K (Long Context)</option>
                      <option value="gpt-4-1106-preview">GPT-4 Turbo 1106 Preview (Latest)</option>
                      <option value="gpt-4-0125-preview">GPT-4 Turbo 0125 Preview (Latest)</option>
                      <option value="gpt-4-0613">GPT-4 0613 (Stable)</option>
                      <option value="gpt-4o-2024-05-13">GPT-4o 2024-05-13 (Latest Vision)</option>
                      <option value="gpt-4o-mini-2024-07-18">GPT-4o Mini 2024-07-18 (Latest)</option>
                      <option value="gpt-4-all">GPT-4 All (Multimodal)</option>
                      <option value="gpt-4o-2024-07-18">GPT-4o 2024-07-18 (Latest Stable)</option>
                      <option value="gpt-4o-mini-2024-07-18">GPT-4o Mini 2024-07-18 (Latest Stable)</option>
                    </optgroup>
                    
                    <optgroup label="Latest GPT-3.5 Models (2024-2025)">
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fast & Reliable)</option>
                      <option value="gpt-3.5-turbo-16k">GPT-3.5 Turbo 16K (Extended Context)</option>
                      <option value="gpt-3.5-turbo-instruct">GPT-3.5 Turbo Instruct (Instruction Following)</option>
                      <option value="gpt-3.5-turbo-1106">GPT-3.5 Turbo 1106 (Latest Version)</option>
                      <option value="gpt-3.5-turbo-0125">GPT-3.5 Turbo 0125 (Latest Version)</option>
                      <option value="gpt-3.5-turbo-0613">GPT-3.5 Turbo 0613 (Stable)</option>
                    </optgroup>
                    
                    <optgroup label="Specialized OpenAI Models">
                      <option value="gpt-4-vision-preview">GPT-4 Vision (Image + Text)</option>
                    </optgroup>
                    
                    <optgroup label="Other AI Models">
                      <option value="claude-3">Claude-3 (Anthropic)</option>
                      <option value="gemini-pro">Gemini Pro (Google)</option>
                    </optgroup>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Response Delay (seconds)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={formData.responseDelay}
                    onChange={(e) => setFormData({ ...formData, responseDelay: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </>
            )}
          </div>
          
          {formData.messageOption !== 'ai_assistant' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                AI Context/Instructions
              </label>
              <textarea
                value={formData.aiContext}
                onChange={(e) => setFormData({ ...formData, aiContext: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Provide context or instructions for the AI assistant..."
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                This will help the AI understand how to respond to user queries
              </p>
            </div>
          )}
        </div>
      )}



      {/* Message Option Specific Sections */}
      
      {/* Reply Buttons Section */}
      {formData.messageOption === 'reply_buttons' && (
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-green-900 dark:text-green-100 mb-4 flex items-center">
            <MousePointer className="h-5 w-5 mr-2" />
            Reply Buttons Configuration
          </h3>
          
          <div className="space-y-4">
            {formData.replyButtons.map((button, index) => (
              <div key={index} className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-700 rounded-lg">
                <input
                  type="text"
                  value={button.text}
                  onChange={(e) => {
                    const newButtons = [...formData.replyButtons];
                    newButtons[index].text = e.target.value;
                    setFormData({ ...formData, replyButtons: newButtons });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Button text"
                />
                <input
                  type="text"
                  value={button.response}
                  onChange={(e) => {
                    const newButtons = [...formData.replyButtons];
                    newButtons[index].response = e.target.value;
                    setFormData({ ...formData, replyButtons: newButtons });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Response message"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newButtons = formData.replyButtons.filter((_, i) => i !== index);
                    setFormData({ ...formData, replyButtons: newButtons });
                  }}
                  className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            
            <button
              type="button"
              onClick={() => {
                const newButtons = [...formData.replyButtons, { text: '', response: '' }];
                setFormData({ ...formData, replyButtons: newButtons });
              }}
              className="w-full px-4 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-700 dark:hover:bg-green-900/40"
            >
              + Add Reply Button
            </button>
          </div>
        </div>
      )}

      {/* Button Links Section */}
      {formData.messageOption === 'button_links' && (
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-purple-900 dark:text-purple-100 mb-4 flex items-center">
            <MousePointer className="h-5 w-5 mr-2" />
            Button Links (CTA) Configuration
          </h3>
          
          <div className="space-y-4">
            {formData.buttonLinks.map((button, index) => (
              <div key={index} className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-700 rounded-lg">
                <input
                  type="text"
                  value={button.text}
                  onChange={(e) => {
                    const newButtons = [...formData.buttonLinks];
                    newButtons[index].text = e.target.value;
                    setFormData({ ...formData, buttonLinks: newButtons });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Button text"
                />
                <input
                  type="url"
                  value={button.url}
                  onChange={(e) => {
                    const newButtons = [...formData.buttonLinks];
                    newButtons[index].url = e.target.value;
                    setFormData({ ...formData, buttonLinks: newButtons });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="URL to redirect to"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newButtons = formData.buttonLinks.filter((_, i) => i !== index);
                    setFormData({ ...formData, buttonLinks: newButtons });
                  }}
                  className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            
            <button
              type="button"
              onClick={() => {
                const newButtons = [...formData.buttonLinks, { text: '', url: '' }];
                setFormData({ ...formData, buttonLinks: newButtons });
              }}
              className="w-full px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 border border-purple-300 rounded-md hover:bg-purple-200 dark:text-purple-400 dark:bg-purple-900/20 dark:border-purple-700 dark:hover:bg-purple-900/40"
            >
              + Add Button Link
            </button>
          </div>
        </div>
      )}

      {/* Files Section */}
      {formData.messageOption === 'file' && (
        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-orange-900 dark:text-orange-100 mb-4 flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            File Configuration
          </h3>
          
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <strong>New Feature:</strong> You can now upload files directly or enter file URLs. 
              Uploaded files will be stored securely and can be shared via WhatsApp.
            </div>
          </div>
          
          <div className="space-y-4">
            {formData.files.map((file, index) => (
              <div key={index} className="p-3 bg-white dark:bg-gray-700 rounded-lg space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={file.name}
                    onChange={(e) => {
                      const newFiles = [...formData.files];
                      newFiles[index].name = e.target.value;
                      setFormData({ ...formData, files: newFiles });
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="File name"
                  />
                  <select
                    value={file.type}
                    onChange={(e) => {
                      const newFiles = [...formData.files];
                      newFiles[index].type = e.target.value;
                      setFormData({ ...formData, files: newFiles });
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="document">Document</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const newFiles = formData.files.filter((_, i) => i !== index);
                      setFormData({ ...formData, files: newFiles });
                    }}
                    className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                
                {/* File Upload Section */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Upload File or Enter URL
                  </label>
                  
                  {/* File Upload Component */}
                  <div className="mb-2">
                    <FileUpload
                      multiple={false}
                      accept={getAcceptTypes(file.type)}
                      maxSize={50 * 1024 * 1024} // 50MB
                      onUploadComplete={(uploadedFiles) => handleFileUpload(index, uploadedFiles)}
                      onUploadError={(error) => {
                        toast.error(`Upload failed: ${error}`);
                        setUploadingFiles(prev => ({ ...prev, [index]: false }));
                      }}
                      uploadType="general"
                      compact={true}
                      autoUpload={true}
                    />
                  </div>
                  
                                     {/* URL Input (Alternative) */}
                   <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                     Or enter file URL directly:
                   </div>
                   <input
                     type="url"
                     value={file.url}
                     onChange={(e) => {
                       const newFiles = [...formData.files];
                       newFiles[index].url = e.target.value;
                       setFormData({ ...formData, files: newFiles });
                     }}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                     placeholder="https://example.com/file.pdf"
                   />
                   
                   {/* File Preview */}
                   {file.url && (
                     <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-600 rounded-md">
                       <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                         File Preview:
                       </div>
                       <div className="flex items-center space-x-2">
                         <File className="h-4 w-4 text-gray-500" />
                         <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                           {file.name || 'File'}
                         </span>
                         <span className="text-xs text-gray-500">
                           ({file.type})
                         </span>
                       </div>
                       {file.url && (
                         <div className="mt-1">
                           <a
                             href={file.url}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                           >
                             View File →
                           </a>
                         </div>
                       )}
                     </div>
                   )}
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={() => {
                const newFiles = [...formData.files, { name: '', url: '', type: 'document' }];
                setFormData({ ...formData, files: newFiles });
              }}
              className="w-full px-4 py-2 text-sm font-medium text-orange-700 bg-orange-100 border border-orange-300 rounded-md hover:bg-orange-200 dark:text-orange-400 dark:bg-orange-900/20 dark:border-orange-700 dark:hover:bg-orange-900/40"
            >
              + Add File
            </button>
          </div>
        </div>
      )}

      {/* Options Section */}
      {formData.messageOption === 'options' && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-indigo-900 dark:text-indigo-100 mb-4 flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Options Configuration
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subtext (Optional)
              </label>
              <textarea
                value={formData.subtext}
                onChange={(e) => setFormData({ ...formData, subtext: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Additional text to display with options..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Submit Button Text
              </label>
              <input
                type="text"
                value={formData.submitButtonText}
                onChange={(e) => setFormData({ ...formData, submitButtonText: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Submit"
              />
            </div>
            
            {formData.options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-700 rounded-lg">
                <input
                  type="text"
                  value={option.text}
                  onChange={(e) => {
                    const newOptions = [...formData.options];
                    newOptions[index].text = e.target.value;
                    setFormData({ ...formData, options: newOptions });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Option text"
                />
                <input
                  type="text"
                  value={option.value}
                  onChange={(e) => {
                    const newOptions = [...formData.options];
                    newOptions[index].value = e.target.value;
                    setFormData({ ...formData, options: newOptions });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Option value"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newOptions = formData.options.filter((_, i) => i !== index);
                    setFormData({ ...formData, options: newOptions });
                  }}
                  className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            
            <button
              type="button"
              onClick={() => {
                const newOptions = [...formData.options, { text: '', value: '' }];
                setFormData({ ...formData, options: newOptions });
              }}
              className="w-full px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-100 border border-indigo-300 rounded-md hover:bg-indigo-200 dark:text-indigo-400 dark:bg-indigo-900/20 dark:border-indigo-700 dark:hover:bg-indigo-900/40"
            >
              + Add Option
            </button>
          </div>
        </div>
      )}




      {/* Scheduling */}
      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-purple-900 dark:text-purple-100 mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2" />
          Scheduling & Conditions
        </h3>
        
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="scheduleEnabled"
            checked={formData.scheduleEnabled}
            onChange={(e) => setFormData({ ...formData, scheduleEnabled: e.target.checked })}
            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
          />
          <label htmlFor="scheduleEnabled" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
            Enable scheduling
          </label>
        </div>
        
        {formData.scheduleEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Time
              </label>
              <input
                type="time"
                value={formData.scheduleStart}
                onChange={(e) => setFormData({ ...formData, scheduleStart: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Time
              </label>
              <input
                type="time"
                value={formData.scheduleEnd}
                onChange={(e) => setFormData({ ...formData, scheduleEnd: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Timezone
              </label>
              <select
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="UTC">UTC</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="Europe/London">London</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Template Preview */}
      {(formData.header || formData.response || formData.footer || formData.mediaUrl || formData.replyButtons.length > 0 || formData.buttonLinks.length > 0) && (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border-2 border-dashed border-gray-300 dark:border-gray-600">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            📱 Message Preview
          </h4>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm max-w-sm mx-auto">
            {/* Header */}
            {formData.header && (
              <div className="text-center mb-3">
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded">
                  {formData.header}
                </div>
              </div>
            )}

            {/* Media */}
            {formData.mediaUrl && (
              <div className="mb-3">
                {formData.mediaType === 'image' ? (
                  <div className="text-center">
                    <img 
                      src={formData.mediaUrl} 
                      alt="Media" 
                      className="max-w-full h-32 object-cover rounded-lg mx-auto"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                      📎 Image (Failed to load)
                    </div>
                  </div>
                ) : formData.mediaType === 'video' ? (
                  <div className="text-center">
                    <video 
                      src={formData.mediaUrl} 
                      className="max-w-full h-32 object-cover rounded-lg mx-auto"
                      controls
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                      📎 Video (Failed to load)
                    </div>
                  </div>
                ) : formData.mediaType === 'audio' ? (
                  <div className="text-center">
                    <audio 
                      src={formData.mediaUrl} 
                      controls
                      className="w-full"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                      📎 Audio (Failed to load)
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                      📎 {formData.mediaType} Attachment
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Main Message */}
            {formData.response && (
              <div className="mb-3">
                <div className="text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                  {formData.response}
                </div>
              </div>
            )}

            {/* Reply Buttons */}
            {formData.replyButtons.length > 0 && (
              <div className="mb-3">
                <div className="space-y-1">
                  {formData.replyButtons.map((button, index) => (
                    <div key={index} className="text-center">
                      <button className="w-full px-3 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
                        {button.text || `Button ${index + 1}`}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Button Links */}
            {formData.buttonLinks.length > 0 && (
              <div className="mb-3">
                <div className="space-y-1">
                  {formData.buttonLinks.map((button, index) => (
                    <div key={index} className="text-center">
                      <button className="w-full px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                        {button.text || `Link ${index + 1}`}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            {formData.footer && (
              <div className="text-center mt-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                  {formData.footer}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </form>
  );

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">WhatsApp Auto-Reply Bot</h1>
            <p className="text-gray-600 dark:text-gray-400">Manage FAQs and automatic responses</p>
          </div>
          <div className="flex items-center space-x-2">
            <Bot className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {settings.autoReplyEnabled ? (
                <span className="flex items-center text-green-600">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Active
                </span>
              ) : (
                <span className="flex items-center text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Inactive
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('faqs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'faqs'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span>FAQs</span>
          </button>
          <button
            onClick={() => setActiveTab('auto-replies')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'auto-replies'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <Bot className="w-4 h-4" />
            <span>Auto Replies</span>
          </button>
        </nav>
      </div>

      {/* Settings Panel */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
          <Settings className="h-5 w-5 mr-2" />
          Auto-Reply Settings
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Enable Auto-Reply
            </label>
            <button
              onClick={() => updateSetting('autoReplyEnabled', !settings.autoReplyEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.autoReplyEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.autoReplyEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Enable Greetings
            </label>
            <button
              onClick={() => updateSetting('greetingEnabled', !settings.greetingEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.greetingEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.greetingEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              After Hours Replies
            </label>
            <button
              onClick={() => updateSetting('afterHoursEnabled', !settings.afterHoursEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.afterHoursEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.afterHoursEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Greeting and Business Hours Configuration */}
        <div className="space-y-4">
          {/* Business Hours */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Business Hours Start
              </label>
              <input
                type="time"
                value={settings.businessHoursStart || '09:00'}
                onChange={(e) => updateSetting('businessHoursStart', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Business Hours End
              </label>
              <input
                type="time"
                value={settings.businessHoursEnd || '18:00'}
                onChange={(e) => updateSetting('businessHoursEnd', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Greeting Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Greeting Message
            </label>
            <textarea
              value={settings.greetingMessage || 'Hello! Welcome to Niswa Info Tech. How can we help you today?'}
              onChange={(e) => updateSetting('greetingMessage', e.target.value)}
              rows={3}
              placeholder="Enter your greeting message..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              This message will be sent to new contacts when greeting is enabled.
            </p>
          </div>

          {/* After Hours Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              After Hours Message
            </label>
            <textarea
              value={settings.afterHoursMessage || 'Thank you for contacting us! We are currently outside our business hours (9:00 AM - 6:00 PM). We will get back to you as soon as possible.'}
              onChange={(e) => updateSetting('afterHoursMessage', e.target.value)}
              rows={3}
              placeholder="Enter your after hours message..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              This message will be sent when someone contacts you outside business hours.
            </p>
          </div>

          {/* Abandoned Chat Configuration */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4">
              Abandoned Chat Follow-up
            </h3>
            
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable Abandoned Chat Follow-up
              </label>
              <button
                onClick={() => updateSetting('abandonedChatEnabled', !settings.abandonedChatEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.abandonedChatEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.abandonedChatEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Delay (seconds)
                </label>
                <input
                  type="number"
                  min="0"
                  max="86400"
                  value={settings.abandonedChatDelaySeconds || '0'}
                  onChange={(e) => updateSetting('abandonedChatDelaySeconds', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Time to wait before sending follow-up message (0-86400 seconds, 0 = immediate)
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Abandoned Chat Message
              </label>
              <textarea
                value={settings.abandonedChatMessage || 'Hi there! We noticed you haven\'t responded in a while. Is there anything else we can help you with? Feel free to reach out anytime!'}
                onChange={(e) => updateSetting('abandonedChatMessage', e.target.value)}
                rows={3}
                placeholder="Enter your abandoned chat follow-up message..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                This message will be sent to users who stop chatting after the specified delay.
              </p>
            </div>
          </div>
        </div>
        

      </div>

      {/* Main Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 sm:p-6">
          {/* Add Button */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {activeTab === 'faqs' ? 'Frequently Asked Questions' : 'Automatic Replies'}
            </h3>
            

            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  setShowAddForm(true);
                  setEditingItem(null);
                  resetForm();
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add {activeTab === 'faqs' ? 'FAQ' : 'Auto-Reply'}
              </button>
              
              <button
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </button>
              
              <button
                onClick={() => handleBulkExport()}
                disabled={bulkActionLoading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Export All
              </button>
            </div>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                  {editingItem ? 'Edit' : 'Add'} {activeTab === 'faqs' ? 'FAQ' : 'Auto-Reply'}
                </h4>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingItem(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {activeTab === 'faqs' ? renderFAQForm() : renderAutoReplyForm()}
              
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingItem(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (editingItem ? 'Update' : 'Save')}
                </button>
              </div>
            </div>
          )}



          {/* Bulk Actions Toolbar */}
          {selectedItems.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {selectedItems.length} {activeTab === 'faqs' ? 'FAQ' : 'auto-reply'}{selectedItems.length > 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={() => setSelectedItems([])}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                  >
                    Clear selection
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleBulkActivate}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Activate
                  </button>
                  <button
                    onClick={handleBulkDeactivate}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-yellow-700 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
                  >
                    <Archive className="h-4 w-4 mr-1" />
                    Deactivate
                  </button>
                  <button
                    onClick={handleBulkExport}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Export
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Filter Options */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showInactiveItems}
                  onChange={(e) => setShowInactiveItems(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Show inactive items
                </span>
              </label>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {activeTab === 'faqs' ? faqs.length : autoReplies.length} total items
            </div>
          </div>

          {/* Multiple Replies Summary */}
          {activeTab === 'auto-replies' && groupedAutoReplies.filter(g => g.count > 1).length > 0 && (
            <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 text-orange-600 mr-2" />
                <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  Multiple Auto Replies Found:
                </span>
              </div>
              <div className="mt-2 text-xs text-orange-700 dark:text-orange-300">
                {groupedAutoReplies
                  .filter(g => g.count > 1)
                  .map(group => (
                    <span key={group.trigger} className="inline-block mr-3 mb-1">
                      <strong>"{group.trigger}"</strong>: {group.count} replies (Priority: {group.highestPriority} → 1)
                    </span>
                  ))
                }
              </div>
              <div className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                <strong>Note:</strong> When multiple auto replies have the same trigger keyword, they will be sent in priority order (highest priority first). 
                Higher priority numbers (e.g., 10) are processed before lower priority numbers (e.g., 1).
              </div>
            </div>
          )}

          {/* Data Table */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      <button
                        onClick={handleSelectAll}
                        className="flex items-center"
                      >
                        {selectAll ? (
                          <CheckSquare className="h-4 w-4 text-blue-600" />
                        ) : (
                          <Square className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </th>
                    {activeTab === 'faqs' ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Question
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Language
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Trigger
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Response
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Language
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                      </>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {(Array.isArray(activeTab === 'faqs' ? faqs : autoReplies) ? (activeTab === 'faqs' ? faqs : autoReplies) : []).filter(item => showInactiveItems || item.is_active === 1 || item.is_active === true).map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      item.is_active === 0 || item.is_active === false 
                        ? 'opacity-60 bg-gray-50 dark:bg-gray-700' 
                        : ''
                    }`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleSelectItem(item.id)}
                          className="flex items-center"
                        >
                          {selectedItems.includes(item.id) ? (
                            <CheckSquare className="h-4 w-4 text-blue-600" />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </td>
                      {activeTab === 'faqs' ? (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {item.question}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {item.answer.substring(0, 100)}...
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {item.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              <Globe className="h-3 w-3 mr-1" />
                              {item.language.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {item.priority}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                              item.is_active === 1 || item.is_active === true 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {item.is_active === 1 || item.is_active === true ? '✅ Active' : '❌ Inactive'}
                            </span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {item.trigger_keyword}
                              {/* Show indicator for multiple replies with same trigger */}
                              {groupedAutoReplies.find(g => g.trigger === item.trigger_keyword)?.count > 1 && (
                                <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                  {groupedAutoReplies.find(g => g.trigger === item.trigger_keyword)?.count} replies
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Type: {item.message_option || 'text'}
                              {/* Show priority order for multiple replies */}
                              {groupedAutoReplies.find(g => g.trigger === item.trigger_keyword)?.count > 1 && (
                                <span className="ml-2 text-orange-600 dark:text-orange-400">
                                  Priority: {item.priority || 1}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {item.response_message ? item.response_message.substring(0, 100) : item.response ? item.response.substring(0, 100) : 'No response content'}...
                            </div>
                            {item.message_option === 'file' && item.files && (
                              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                📎 {item.files.length} file(s) attached
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              <Globe className="h-3 w-3 mr-1" />
                              {item.language?.toUpperCase() || 'EN'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {item.priority || 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                              item.is_active === 1 || item.is_active === true 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {item.is_active === 1 || item.is_active === true ? '✅ Active' : '❌ Inactive'}
                            </span>
                          </td>
                        </>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id, activeTab === 'faqs' ? 'faq' : 'auto-reply')}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {(Array.isArray(activeTab === 'faqs' ? faqs : autoReplies) ? (activeTab === 'faqs' ? faqs : autoReplies) : []).filter(item => showInactiveItems || item.is_active === 1 || item.is_active === true).length === 0 && (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    {showInactiveItems 
                      ? `No ${activeTab === 'faqs' ? 'FAQs' : 'auto-replies'} found.`
                      : `No active ${activeTab === 'faqs' ? 'FAQs' : 'auto-replies'} found.`
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Import {activeTab === 'faqs' ? 'FAQs' : 'Auto-Replies'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select JSON File
                  </label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Upload a JSON file with {activeTab === 'faqs' ? 'FAQ' : 'auto-reply'} data
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Import Type
                  </label>
                  <select
                    value={importType}
                    onChange={(e) => setImportType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="faqs">FAQs</option>
                    <option value="auto-replies">Auto-Replies</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={!importFile || bulkActionLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {bulkActionLoading ? 'Importing...' : 'Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppAutoReply;
